"use client"

import * as React from "react"
import {ChevronsUpDown, Plus, Loader2} from "lucide-react"

import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuShortcut,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    useSidebar,
} from "@/components/ui/sidebar"
import {Team, useTeams} from "@/features/teams/hooks/useTeams"
import {Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle} from "@/components/ui/dialog"
import {Button} from "@/components/ui/button"
import {Input} from "@/components/ui/input"
import {Label} from "@/components/ui/label"
import {useState, useEffect} from "react"
import {toast} from "@/hooks/use-toast"
import {PlaceholderLogo} from "@/components/placeholder-logo"

export function TeamSwitcher({user}) {
    const {isMobile} = useSidebar()
    const {teams, isLoading, createTeam} = useTeams()
    const [activeTeam, setActiveTeam] = React.useState<Team>(null)
    const [isCreateTeamDialogOpen, setIsCreateTeamDialogOpen] = useState(false)
    const [newTeamName, setNewTeamName] = useState("")
    const [isCreatingTeam, setIsCreatingTeam] = useState(false)

    // Set the first team as active when teams are loaded
    React.useEffect(() => {
        if (teams && teams.length > 0 && !activeTeam) {
            setActiveTeam(teams[0])
        }
    }, [teams, activeTeam])

    const handleCreateTeam = async () => {
        if (!newTeamName.trim()) {
            toast({
                title: "Error",
                description: "Team name cannot be empty",
                variant: "destructive",
            })
            return
        }

        setIsCreatingTeam(true)
        try {
            await createTeam.mutateAsync(newTeamName)
            setNewTeamName("")
            setIsCreateTeamDialogOpen(false)
        } catch (error) {
            console.error("Failed to create team:", error)
        } finally {
            setIsCreatingTeam(false)
        }
    }

    // Generate placeholder team when loading
    const placeholderTeam = {
        name: "Loading...",
        logo: Loader2,
        plan: "..."
    }

    const formatTeam = (team: any) => ({
        ...team,
        logo: PlaceholderLogo,
        plan: "Team"
    })

    const displayTeam = activeTeam ? formatTeam(activeTeam) : placeholderTeam

    if (!user.teams) {
        return <></>;
    }

    return (
        <SidebarMenu>
            <SidebarMenuItem>
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <SidebarMenuButton
                            size="lg"
                            className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                        >
                            <div
                                className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
                                {isLoading ? (
                                    <Loader2 className="size-4 animate-spin"/>
                                ) : (
                                    <displayTeam.logo className="size-4"/>
                                )}
                            </div>
                            <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-semibold">
                  {displayTeam.name}
                </span>
                                <span className="truncate text-xs">{displayTeam.plan}</span>
                            </div>
                            <ChevronsUpDown className="ml-auto"/>
                        </SidebarMenuButton>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                        className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
                        align="start"
                        side={isMobile ? "bottom" : "right"}
                        sideOffset={4}
                    >
                        <DropdownMenuLabel className="text-xs text-muted-foreground">
                            Teams
                        </DropdownMenuLabel>
                        {isLoading ? (
                            <DropdownMenuItem disabled className="gap-2 p-2">
                                <Loader2 className="size-4 animate-spin"/>
                                Loading teams...
                            </DropdownMenuItem>
                        ) : (
                            user.teams.map((team, index) => (
                                <DropdownMenuItem
                                    key={team.id}
                                    onClick={() => setActiveTeam(team)}
                                    className="gap-2 p-2"
                                >
                                    <div className="flex size-6 items-center justify-center rounded-sm border">
                                        <PlaceholderLogo className="size-4 shrink-0"/>
                                    </div>
                                    {team.name}
                                    <DropdownMenuShortcut>⌘{index + 1}</DropdownMenuShortcut>
                                </DropdownMenuItem>
                            ))
                        )}
                        <DropdownMenuSeparator/>
                        <DropdownMenuItem
                            className="gap-2 p-2"
                            onClick={() => setIsCreateTeamDialogOpen(true)}
                        >
                            <div className="flex size-6 items-center justify-center rounded-md border bg-background">
                                <Plus className="size-4"/>
                            </div>
                            <div className="font-medium text-muted-foreground">Add team</div>
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </SidebarMenuItem>

            <Dialog open={isCreateTeamDialogOpen} onOpenChange={setIsCreateTeamDialogOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Create new team</DialogTitle>
                        <DialogDescription>
                            Add a new team to collaborate with your colleagues
                        </DialogDescription>
                    </DialogHeader>
                    <div className="grid gap-4 py-4">
                        <div className="grid gap-2">
                            <Label htmlFor="team-name">Team name</Label>
                            <Input
                                id="team-name"
                                placeholder="Enter team name"
                                value={newTeamName}
                                onChange={(e) => setNewTeamName(e.target.value)}
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setIsCreateTeamDialogOpen(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleCreateTeam}
                            disabled={isCreatingTeam || !newTeamName.trim()}
                        >
                            {isCreatingTeam ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin"/>
                                    Creating...
                                </>
                            ) : (
                                'Create Team'
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </SidebarMenu>
    )
}
